import {computed, Injectable, Signal} from '@angular/core';
import {toObservable} from '@angular/core/rxjs-interop';
import {Observable, Subject} from 'rxjs';
import {Store, StoreConfig} from 'signalstory';
import {IdbPersistenceStore} from './plugins/idb/idb-persistence.plugin';
import {useStoreHooks, UseStoreHooksPlugin} from './plugins/store-hooks.plugin';

export type SignalStoreType = 'Global' | 'Feature';
export type SignalStoreConfig<TState> = Pick<StoreConfig<TState>, 'plugins' | 'injector' | 'stateEqualityFn'> & {
	isEntityStore?: boolean;
};

type PatchStatePatches<TState> = Partial<TState> | ((state: TState) => Partial<TState>);

/* ------------ Providers / Services ------------ */
/* ---------------- Properties ---------------- */
/* ---------------- Computed ---------------- */
/* ---------------- Constructor ---------------- */
/* -------------------------------- Http Methods -------------------------------- */
/* -------------------------------- State Methods -------------------------------- */

/* -------------------------------- Private Methods -------------------------------- */

@Injectable({providedIn: 'root'})
export abstract class SignalStore<TState extends object> extends Store<TState> implements IdbPersistenceStore<TState> {
	public readonly stateObservable$: Observable<TState>;
	public readonly isLoadedFromStorage$: Signal<boolean>;
	public readonly isLoadedFromStorageObs$: Observable<boolean>;

	protected constructor(
		/**
		 * The name of the store (optional, default: constructor name).
		 */
		name: string,
		/**
		 * The type of the store, 'Global' or 'Feature'.
		 */
		type: SignalStoreType,
		/**
		 * The initial state of the store.
		 */
		initialState: TState,
		config?: Partial<SignalStoreConfig<TState>>,
	) {
		super({
			initialState,
			name: `[${type}] ${name}`,
			plugins: [...(config?.plugins || []), useStoreHooks({})],
			injector: config?.injector,
			stateEqualityFn: config?.stateEqualityFn,
		});
		this.stateObservable$ = toObservable(this.state);

		// get the hooks plugin and set the isLoadedFromStorage$ and isLoadedFromStorageObs$ signals
		const hooksPlugin = this.config.plugins.find(plugin => plugin.name === 'store-hooks') as UseStoreHooksPlugin<TState>;
		if (hooksPlugin) {
			this.isLoadedFromStorage$ = hooksPlugin.isLoadedFromStorage$;
			this.isLoadedFromStorageObs$ = hooksPlugin.isLoadedFromStorageObs$;
		}

		// Set the logout emitter
		SignalStore.logoutEmitter$.subscribe(() => {
			const state = this.onLogout?.(this.state(), initialState);
			if (state) {
				this.set(state);
			}
		});
	}

	// Optional methods for the store
	// IDB Persistence
	/** Hook called when the store is loaded from the database (only if the store is persisted with the idb persistence plugin) */
	public onStoreLoaded?(state: TState): void;

	/** Hook called when the store is updated (only if the store is persisted with the idb persistence plugin) */
	public onStoreUpdated?(state: TState): void;

	/** Hook called when the store is reset (only if the store is persisted with the idb persistence plugin) */
	public onStoreReset?(state: TState): TState;

	public static logoutEmitter$ = new Subject<void>();
	public onLogout?(state: TState = this.state(), initialState: TState = this.config.initialState): TState {
		return {...initialState};
	}

	public patchState(patches: PatchStatePatches<TState>, commandName?: string): void {
		this.update(state => ({...state, ...(typeof patches === 'function' ? patches(state) : patches)}), commandName);
	}

	/**
	 * Selects a value from the store state.
	 * @param selector - A function that takes the store state and returns the selected value.
	 * @returns A signal that emits the selected value.
	 */
	public select<T>(selector: (state: TState) => T): Signal<T> {
		return computed(() => selector(this.state()));
	}
}
